/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { RestService } from '../rest/rest.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { RestError } from '../rest/rest.types';

export interface SavedSearchFilters {
	[key: string]: any;
}

export interface SavedSearch {
	pk: string;
	user: string;
	name: string;
	filters: SavedSearchFilters | string; // Can be object or legacy string format
}

export interface ParsedSavedSearchFilters {
	location?: {
		lat: string;
		long: string;
		range: number;
		address: string;
		address_2: string;
		city: string;
		state: string;
		country: string;
		postal: string;
	};
	topCat?: string;
	askingPrice?: { lower: number; upper: number };
	leasePrice?: { lower: number; upper: number };
	saleTypes?: string[];
	skills?: string[];
	breed?: string[];
	color?: string[];
	gender?: string[];
	[key: string]: any;
}

export interface SavedSearchListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: SavedSearch[];
}

export interface SavedSearchCreateInput {
	name: string;
	filters: SavedSearchFilters;
}

@Injectable({
	providedIn: 'root',
})
export class SavedSearchService {
	private savedSearchesSubject = new BehaviorSubject<SavedSearch[]>([]);
	public savedSearches$ = this.savedSearchesSubject.asObservable();

	constructor(private restService: RestService) {}

	/**
	 * Get all saved searches for a user
	 */
	getSavedSearches(userPk: string): Observable<SavedSearch[]> {
		const url = `${environment.base_api_url}/auth/users/${userPk}/saved_searches/`;
		return this.restService.get(url).pipe(
			map((response: SavedSearchListResponse | RestError) => {
				if (response instanceof RestError) {
					console.error('Error fetching saved searches:', response);
					return [];
				}
				return response.results || [];
			}),
			tap((searches) => {
				console.log('Loaded saved searches:', searches);
				this.savedSearchesSubject.next(searches);
			}),
			catchError((error) => {
				console.error('Error fetching saved searches:', error);
				return of([]);
			})
		);
	}

	/**
	 * Create a new saved search
	 */
	createSavedSearch(userPk: string, data: SavedSearchCreateInput): Observable<SavedSearch | null> {
		const url = `${environment.base_api_url}/auth/users/${userPk}/saved_searches/`;
		// Serialize filters to the legacy string format expected by the server
		const payload = {
			user: userPk,
			name: data.name,
			filters: this.serializeFiltersToString(data.filters),
		};
		return this.restService.post(url, payload).pipe(
			map((response: SavedSearch | RestError) => {
				if (response instanceof RestError) {
					console.error('Error creating saved search:', response);
					return null;
				}
				// Add to local state
				const current = this.savedSearchesSubject.getValue();
				this.savedSearchesSubject.next([...current, response]);
				return response;
			}),
			catchError((error) => {
				console.error('Error creating saved search:', error);
				return of(null);
			})
		);
	}

	/**
	 * Update an existing saved search
	 */
	updateSavedSearch(userPk: string, searchPk: string, data: Partial<SavedSearchCreateInput>): Observable<SavedSearch | null> {
		const url = `${environment.base_api_url}/auth/users/${userPk}/saved_searches/${searchPk}/`;
		// Serialize filters to the legacy string format expected by the server
		const payload: { user: string; name?: string; filters?: string } = {
			user: userPk,
		};
		if (data.name) {
			payload.name = data.name;
		}
		if (data.filters) {
			payload.filters = this.serializeFiltersToString(data.filters);
		}
		return this.restService.patch(url, payload).pipe(
			map((response: SavedSearch | RestError) => {
				if (response instanceof RestError) {
					console.error('Error updating saved search:', response);
					return null;
				}
				// Update local state
				const current = this.savedSearchesSubject.getValue();
				const updated = current.map((s) => (s.pk === searchPk ? response : s));
				this.savedSearchesSubject.next(updated);
				return response;
			}),
			catchError((error) => {
				console.error('Error updating saved search:', error);
				return of(null);
			})
		);
	}

	/**
	 * Delete a saved search
	 */
	deleteSavedSearch(userPk: string, searchPk: string): Observable<boolean> {
		const url = `${environment.base_api_url}/auth/users/${userPk}/saved_searches/${searchPk}/`;
		return this.restService.delete(url).pipe(
			map((response: any | RestError) => {
				if (response instanceof RestError) {
					console.error('Error deleting saved search:', response);
					return false;
				}
				// Remove from local state
				const current = this.savedSearchesSubject.getValue();
				this.savedSearchesSubject.next(current.filter((s) => s.pk !== searchPk));
				return true;
			}),
			catchError((error) => {
				console.error('Error deleting saved search:', error);
				return of(false);
			})
		);
	}

	/**
	 * Clear saved searches from state (on logout)
	 */
	clearSavedSearches(): void {
		this.savedSearchesSubject.next([]);
	}

	/**
	 * Get current saved searches value
	 */
	get savedSearches(): SavedSearch[] {
		return this.savedSearchesSubject.getValue();
	}

	/**
	 * Parse legacy URL query string format filters into structured object
	 * Example: "location[]={lat:}&location[]={long:}&breed[]=uuid1&breed[]=uuid2&askingPrice_min=0&askingPrice_max=50000"
	 */
	parseLegacyFilters(filterString: string): ParsedSavedSearchFilters {
		const result: ParsedSavedSearchFilters = {};

		if (!filterString || typeof filterString !== 'string') {
			return result;
		}

		// Split by & and parse each part
		const parts = filterString.split('&').filter((p) => p.length > 0);

		// Temporary storage for array values and location parts
		const arrays: { [key: string]: string[] } = {};
		const locationParts: { [key: string]: string } = {};
		const rangeValues: { [key: string]: { min?: number; max?: number } } = {};

		for (const part of parts) {
			const [rawKey, rawValue] = part.split('=');
			if (!rawKey) continue;

			const value = decodeURIComponent(rawValue || '');

			// Handle location[] format: location[]={lat:value}
			if (rawKey === 'location[]') {
				const match = value.match(/\{(\w+):([^}]*)\}/);
				if (match) {
					const [, field, fieldValue] = match;
					locationParts[field] = fieldValue || '';
				}
				continue;
			}

			// Handle array format: breed[]=uuid
			if (rawKey.endsWith('[]')) {
				const key = rawKey.slice(0, -2);
				if (!arrays[key]) {
					arrays[key] = [];
				}
				if (value) {
					arrays[key].push(value);
				}
				continue;
			}

			// Handle range format: askingPrice_min=0, askingPrice_max=50000
			const rangeMatch = rawKey.match(/^(\w+)_(min|max)$/);
			if (rangeMatch) {
				const [, key, bound] = rangeMatch;
				if (!rangeValues[key]) {
					rangeValues[key] = {};
				}
				const numValue = parseFloat(value);
				if (!isNaN(numValue)) {
					rangeValues[key][bound as 'min' | 'max'] = numValue;
				}
				continue;
			}

			// Handle simple key=value
			result[rawKey] = value;
		}

		// Process location parts
		if (Object.keys(locationParts).length > 0) {
			result.location = {
				lat: locationParts.lat || '',
				long: locationParts.long || '',
				range: parseInt(locationParts.range, 10) || 0,
				address: locationParts.address || '',
				address_2: locationParts.address_2 || '',
				city: locationParts.city || '',
				state: locationParts.state || '',
				country: locationParts.country === 'undefined' ? '' : locationParts.country || '',
				postal: locationParts.postal || '',
			};
		}

		// Process arrays
		for (const [key, values] of Object.entries(arrays)) {
			if (values.length > 0) {
				result[key] = values;
			}
		}

		// Process range values
		for (const [key, bounds] of Object.entries(rangeValues)) {
			if (bounds.min !== undefined || bounds.max !== undefined) {
				result[key] = {
					lower: bounds.min ?? 0,
					upper: bounds.max ?? undefined,
				};
			}
		}

		console.log('Parsed legacy filters:', result);
		return result;
	}

	/**
	 * Get parsed filters from a saved search, handling both legacy string and new object formats
	 */
	getParsedFilters(savedSearch: SavedSearch): ParsedSavedSearchFilters {
		if (typeof savedSearch.filters === 'string') {
			return this.parseLegacyFilters(savedSearch.filters);
		}
		return savedSearch.filters as ParsedSavedSearchFilters;
	}

	/**
	 * Serialize filters object to the legacy URL query string format expected by the server
	 * This converts { breed: ['uuid1', 'uuid2'], askingPrice: { lower: 0, upper: 50000 } }
	 * to "breed[]=uuid1&breed[]=uuid2&askingPrice_min=0&askingPrice_max=50000"
	 */
	serializeFiltersToString(filters: { [key: string]: any }): string {
		const parts: string[] = [];

		for (const [key, value] of Object.entries(filters)) {
			if (value === null || value === undefined) {
				continue;
			}

			// Handle location object specially
			if (key === 'location' && typeof value === 'object' && !Array.isArray(value)) {
				const loc = value as any;
				parts.push(`location[]={lat:${loc.lat || ''}}`);
				parts.push(`location[]={long:${loc.long || ''}}`);
				parts.push(`location[]={range:${loc.range || 0}}`);
				parts.push(`location[]={address:${loc.address || ''}}`);
				parts.push(`location[]={address_2:${loc.address_2 || ''}}`);
				parts.push(`location[]={city:${loc.city || ''}}`);
				parts.push(`location[]={state:${loc.state || ''}}`);
				parts.push(`location[]={country:${loc.country || 'undefined'}}`);
				parts.push(`location[]={postal:${loc.postal || ''}}`);
				continue;
			}

			// Handle arrays (breed[], skills[], color[], gender[], saleTypes[])
			if (Array.isArray(value)) {
				for (const item of value) {
					parts.push(`${key}[]=${encodeURIComponent(item)}`);
				}
				continue;
			}

			// Handle range objects with lower/upper (askingPrice, leasePrice)
			if (typeof value === 'object' && ('lower' in value || 'upper' in value)) {
				if (value.lower !== undefined && value.lower !== null) {
					parts.push(`${key}_min=${value.lower}`);
				}
				if (value.upper !== undefined && value.upper !== null) {
					parts.push(`${key}_max=${value.upper}`);
				}
				continue;
			}

			// Handle simple values (topCat, etc.)
			parts.push(`${key}=${encodeURIComponent(String(value))}`);
		}

		return parts.join('&');
	}
}
